import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Edit, Trash2, Tag, Loader2, Check, X, ToggleLeft, ToggleRight, Search, Link2, Globe, Mail, Table2, MessageCircle, Megaphone, MapPin, Users, Instagram, Linkedin, Facebook } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ICON_MAP = {
  facebook: Facebook,
  globe: Globe,
  mail: Mail,
  table: Table2,
  "message-circle": MessageCircle,
  megaphone: Megaphone,
  "map-pin": MapPin,
  users: Users,
  edit: Edit,
  tag: Tag,
  instagram: Instagram,
  linkedin: Linkedin,
  link2: Link2,
};

const ICON_OPTIONS = [
  { value: "tag", label: "Tag" },
  { value: "globe", label: "Globe" },
  { value: "mail", label: "Mail" },
  { value: "facebook", label: "Facebook" },
  { value: "instagram", label: "Instagram" },
  { value: "linkedin", label: "LinkedIn" },
  { value: "message-circle", label: "Message" },
  { value: "megaphone", label: "Megaphone" },
  { value: "map-pin", label: "Map Pin" },
  { value: "users", label: "Users" },
  { value: "table", label: "Table" },
  { value: "link2", label: "Link" },
];

const COLOR_OPTIONS = [
  "#6366F1", "#EF4444", "#F59E0B", "#10B981", "#3B82F6",
  "#8B5CF6", "#EC4899", "#14B8A6", "#F97316", "#6B7280",
  "#1877F2", "#25D366", "#0A66C2", "#E4405F", "#0F9D58",
];

function SourceIcon({ iconName, color, size = 18 }) {
  const IconComp = ICON_MAP[iconName] || Tag;
  return <IconComp size={size} style={{ color: color || "#6B7280" }} />;
}

export default function LeadSourceManagement() {
  const navigate = useNavigate();
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSource, setEditingSource] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingSource, setDeletingSource] = useState(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    color: "#6366F1",
    icon: "tag",
  });

  const fetchSources = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/lead-sources`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setSources(data.sources || []);
      }
    } catch (err) {
      console.error("Error fetching sources:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSources(); }, [fetchSources]);

  const openCreate = () => {
    setEditingSource(null);
    setFormData({ name: "", color: "#6366F1", icon: "tag" });
    setDialogOpen(true);
  };

  const openEdit = (source) => {
    setEditingSource(source);
    setFormData({ name: source.name, color: source.color || "#6366F1", icon: source.icon || "tag" });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error("Source name is required");
      return;
    }
    setSaving(true);
    try {
      const url = editingSource
        ? `${BACKEND_URL}/api/lead-sources/${editingSource.source_id}`
        : `${BACKEND_URL}/api/lead-sources`;
      const method = editingSource ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(formData),
      });

      if (res.ok) {
        toast.success(editingSource ? "Source updated" : "Source created");
        setDialogOpen(false);
        fetchSources();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to save");
      }
    } catch (err) {
      toast.error("Network error");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (source) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/lead-sources/${source.source_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ is_active: !source.is_active }),
      });
      if (res.ok) {
        toast.success(source.is_active ? "Source deactivated" : "Source activated");
        fetchSources();
      }
    } catch (err) {
      toast.error("Failed to update source");
    }
  };

  const handleDelete = async () => {
    if (!deletingSource) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/lead-sources/${deletingSource.source_id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (res.ok) {
        toast.success("Source deleted");
        setDeleteDialogOpen(false);
        setDeletingSource(null);
        fetchSources();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to delete");
      }
    } catch (err) {
      toast.error("Network error");
    }
  };

  const filtered = sources.filter(s =>
    s.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const systemSources = filtered.filter(s => s.is_system);
  const customSources = filtered.filter(s => !s.is_system);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/settings")}
          className="rounded-full"
          data-testid="back-to-settings-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Lead Source Management</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Configure lead sources available across your CRM. Custom sources will appear in all lead forms and filters.
          </p>
        </div>
        <Button
          onClick={openCreate}
          className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl"
          data-testid="add-source-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Source
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Search sources..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          className="pl-10 bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 rounded-xl"
          data-testid="search-sources-input"
        />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-indigo-600">{sources.length}</p>
            <p className="text-xs text-slate-500">Total Sources</p>
          </CardContent>
        </Card>
        <Card className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-emerald-600">{sources.filter(s => s.is_active).length}</p>
            <p className="text-xs text-slate-500">Active</p>
          </CardContent>
        </Card>
        <Card className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-amber-600">{customSources.length}</p>
            <p className="text-xs text-slate-500">Custom</p>
          </CardContent>
        </Card>
      </div>

      {/* Custom Sources */}
      {customSources.length > 0 && (
        <Card className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold text-slate-900 dark:text-white flex items-center gap-2">
              <Tag className="w-5 h-5 text-amber-500" />
              Custom Sources
              <Badge className="bg-amber-100 text-amber-700 text-xs">{customSources.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="divide-y divide-slate-100 dark:divide-slate-700">
              {customSources.map(source => (
                <SourceRow
                  key={source.source_id}
                  source={source}
                  onEdit={() => openEdit(source)}
                  onToggle={() => handleToggleActive(source)}
                  onDelete={() => { setDeletingSource(source); setDeleteDialogOpen(true); }}
                />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* System Sources */}
      <Card className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-semibold text-slate-900 dark:text-white flex items-center gap-2">
            <Globe className="w-5 h-5 text-indigo-500" />
            Default Sources
            <Badge className="bg-indigo-100 text-indigo-700 text-xs">{systemSources.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="divide-y divide-slate-100 dark:divide-slate-700">
            {systemSources.map(source => (
              <SourceRow
                key={source.source_id}
                source={source}
                onEdit={() => openEdit(source)}
                onToggle={() => handleToggleActive(source)}
                isSystem
              />
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white dark:bg-slate-800 rounded-2xl max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-slate-900 dark:text-white">
              {editingSource ? "Edit Lead Source" : "Add Lead Source"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Source Name *</Label>
              <Input
                value={formData.name}
                onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Trade Show, Cold Call"
                className="bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-700 rounded-xl"
                data-testid="source-name-input"
              />
            </div>

            {/* Color Picker */}
            <div>
              <Label>Color</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {COLOR_OPTIONS.map(c => (
                  <button
                    key={c}
                    className={`w-7 h-7 rounded-full border-2 transition-all ${formData.color === c ? "border-slate-900 dark:border-white scale-110" : "border-transparent"}`}
                    style={{ backgroundColor: c }}
                    onClick={() => setFormData(prev => ({ ...prev, color: c }))}
                    data-testid={`color-${c}`}
                  />
                ))}
              </div>
            </div>

            {/* Icon Picker */}
            <div>
              <Label>Icon</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {ICON_OPTIONS.map(opt => {
                  const Ic = ICON_MAP[opt.value] || Tag;
                  return (
                    <button
                      key={opt.value}
                      className={`p-2 rounded-lg border transition-all ${formData.icon === opt.value ? "border-indigo-500 bg-indigo-50 dark:bg-indigo-900/30" : "border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700"}`}
                      onClick={() => setFormData(prev => ({ ...prev, icon: opt.value }))}
                      title={opt.label}
                      data-testid={`icon-${opt.value}`}
                    >
                      <Ic size={16} style={{ color: formData.color }} />
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Preview */}
            <div className="bg-slate-50 dark:bg-slate-900 rounded-xl p-3 flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: formData.color + "20" }}>
                <SourceIcon iconName={formData.icon} color={formData.color} size={16} />
              </div>
              <span className="font-medium text-slate-900 dark:text-white">{formData.name || "Source Name"}</span>
              <Badge className="text-xs" style={{ backgroundColor: formData.color + "20", color: formData.color }}>Preview</Badge>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-xl">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl" data-testid="save-source-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {editingSource ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-white dark:bg-slate-800 rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Lead Source</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deletingSource?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white rounded-xl" data-testid="confirm-delete-source-btn">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function SourceRow({ source, onEdit, onToggle, onDelete, isSystem }) {
  return (
    <div className="flex items-center gap-3 py-3 px-1" data-testid={`source-row-${source.source_id}`}>
      <div
        className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: (source.color || "#6B7280") + "20" }}
      >
        <SourceIcon iconName={source.icon} color={source.color} size={16} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm text-slate-900 dark:text-white truncate">{source.name}</span>
          {source.is_system && <Badge className="bg-slate-100 text-slate-500 text-[10px]">System</Badge>}
          {source.type === "integration" && <Badge className="bg-blue-100 text-blue-600 text-[10px]">Integration</Badge>}
          {!source.is_active && <Badge className="bg-red-100 text-red-600 text-[10px]">Inactive</Badge>}
        </div>
      </div>
      <div className="flex items-center gap-1.5">
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggle}
          className="h-8 w-8 rounded-full"
          title={source.is_active ? "Deactivate" : "Activate"}
          data-testid={`toggle-source-${source.source_id}`}
        >
          {source.is_active
            ? <ToggleRight className="w-4 h-4 text-emerald-500" />
            : <ToggleLeft className="w-4 h-4 text-slate-400" />
          }
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={onEdit}
          className="h-8 w-8 rounded-full"
          title="Edit"
          data-testid={`edit-source-${source.source_id}`}
        >
          <Edit className="w-4 h-4 text-slate-500" />
        </Button>
        {!isSystem && onDelete && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onDelete}
            className="h-8 w-8 rounded-full text-red-500 hover:text-red-700"
            title="Delete"
            data-testid={`delete-source-${source.source_id}`}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        )}
      </div>
    </div>
  );
}
